import { useSearch } from '@tanstack/react-router';
import { useCallback, useLayoutEffect, useMemo } from 'react';
import { z } from 'zod';

type QueryParamsReturn<T = string> = {
  param: T;
  setParam: (value: string | number) => void;
  deleteParam: () => void;
  addParam: (value: string) => void;
  deleteSpecific: (value: string) => void;
} & [T, (value: string | number) => void, () => void];

type ValidatorOptions<T extends z.ZodTypeAny> = {
  validator: T;
  enforceDefault?: boolean;
};

// Overload for when validatorOptions object is provided, with optional return type override
function useQueryParams<R = never, T extends z.ZodTypeAny = z.ZodTypeAny>(
  name: string,
  initialValue: string | number | undefined,
  options: ValidatorOptions<T>
): QueryParamsReturn<[R] extends [never] ? z.infer<T> : R>;

// Overload for when validator is provided directly, with optional return type override
function useQueryParams<R = never, T extends z.ZodTypeAny = z.ZodTypeAny>(
  name: string,
  initialValue: string | number | undefined,
  validator: T
): QueryParamsReturn<[R] extends [never] ? z.infer<T> : R>;

// Overload with explicit type parameter for branded types (no validator)
function useQueryParams<R extends string>(name: string, initialValue?: string | number): QueryParamsReturn<R>;

// Overload for backward compatibility (no validator)
function useQueryParams(name: string, initialValue?: string | number): QueryParamsReturn<string>;

// Implementation
function useQueryParams<T extends z.ZodTypeAny>(
  name: string,
  initialValue?: string | number,
  validatorOrOptions?: T | ValidatorOptions<T>
): QueryParamsReturn<string | z.infer<T>> {
  const searchParams = useSearch({ strict: false }) as Record<string, string | undefined>;

  // Extract validator and enforceDefault from options
  const validator =
    validatorOrOptions && 'validator' in validatorOrOptions
      ? validatorOrOptions.validator
      : (validatorOrOptions as T | undefined);
  const enforceDefault =
    validatorOrOptions && 'validator' in validatorOrOptions
      ? (validatorOrOptions.enforceDefault ?? false)
      : validatorOrOptions !== undefined; // If validator is provided directly, default to true for backward compatibility

  // overwrites the current values for this param
  const setParam = useCallback(
    (value: string | number) => {
      const params = new URLSearchParams(window.location.search);
      params.set(name, value.toString());
      const pathname = window.location.pathname;
      const query = params.toString();
      const newUrl = query ? `${pathname}?${query}` : pathname;
      window.history.replaceState({}, '', newUrl);
    },
    [name]
  );

  const validatedParam = useMemo(() => {
    const rawParam = searchParams[name] ?? null;
    const decodedParam = rawParam ? decodeURI(rawParam) : '';

    if (!validator) {
      return decodedParam;
    }

    // Treat empty string as undefined to trigger Zod defaults
    // This ensures validators with .default() will use their default value
    const inputValue = decodedParam || undefined;

    // Use safeParse to handle validation errors gracefully
    // Zod will automatically apply defaults when parsing undefined
    let result = validator.safeParse(inputValue);

    if (result.success) {
      return result.data;
    }

    // If validation failed, try parsing undefined to get default value
    // This handles cases where the URL param is invalid but validator has a default
    if (inputValue !== undefined) {
      const defaultResult = validator.safeParse(undefined);
      if (defaultResult.success) {
        return defaultResult.data;
      }
    }

    // If no default available and validation failed, return empty string
    // This maintains backward compatibility for existing code
    return decodedParam;
  }, [searchParams, name, validator]);

  useLayoutEffect(() => {
    if (initialValue && !searchParams[name]) {
      setParam(initialValue);
    }
  }, [initialValue, name, searchParams, setParam]);

  useLayoutEffect(() => {
    if (!validator) {
      return;
    }

    const rawParam = searchParams[name] ?? null;
    const decodedParam = rawParam ? decodeURI(rawParam) : '';
    const inputValue = decodedParam || undefined;

    const result = validator.safeParse(inputValue);
    if (result.success) {
      if (!rawParam && enforceDefault) {
        const defaultResult = validator.safeParse(undefined);
        if (defaultResult.success) {
          const defaultValue = defaultResult.data;
          setParam(defaultValue);
        }
      }
      return;
    }

    const defaultResult = validator.safeParse(undefined);
    if (defaultResult.success) {
      const defaultValue = defaultResult.data;
      if (decodedParam !== String(defaultValue)) {
        setParam(defaultValue);
      }
    }
  }, [searchParams, name, validator, enforceDefault, setParam]);

  // adds the values to the currently existing values
  const addParam = useCallback(
    (value: string) => {
      const params = new URLSearchParams(window.location.search);
      const existingValues = params.get(name);
      if (existingValues) {
        params.set(name, `${existingValues},${value}`);
      } else {
        params.set(name, value);
      }
      const pathname = window.location.pathname;
      const query = params.toString();
      const newUrl = query ? `${pathname}?${query}` : pathname;
      window.history.replaceState({}, '', newUrl);
    },
    [name]
  );

  // deletes the whole param from the url
  const deleteParam = useCallback(() => {
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.delete(name);
    const pathname = window.location.pathname;
    const query = currentParams.toString();
    const newUrl = query ? `${pathname}?${query}` : pathname;
    window.history.replaceState({}, '', newUrl);
  }, [name]);

  // delete specific value from the url without dropping all
  const deleteSpecific = useCallback(
    (value: string) => {
      const currentParams = new URLSearchParams(window.location.search);
      const existingValues = currentParams.get(name);
      if (existingValues) {
        const filteredValues = existingValues?.split(',').filter((current) => current !== value);
        const newValues = filteredValues?.join(',');
        if (!newValues?.length) {
          currentParams.delete(name);
        } else {
          currentParams.set(name, `${newValues}`);
        }
      } else {
        currentParams.delete(name);
      }
      const pathname = window.location.pathname;
      const query = currentParams.toString();
      const newUrl = query ? `${pathname}?${query}` : pathname;
      window.history.replaceState({}, '', newUrl);
    },
    [name]
  );

  // Create the array with the basic return values
  const array = [validatedParam, setParam, deleteParam] as const;

  // Create the return object that extends array
  const returnValue = Object.assign(array, {
    param: validatedParam,
    setParam,
    deleteParam,
    addParam,
    deleteSpecific,
  });

  return returnValue as QueryParamsReturn<string | z.infer<T>>;
}

export default useQueryParams;
